import {jsPDF} from 'jspdf';
import {getTemplate} from '@/api/feedbackTemplates';

function safeStr(v, fallback = '') {
    const s = (v ?? '').toString().trim();
    return s ? s : fallback;
}

function clampPct(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(100, Math.round(x)));
}

function fileSafe(s) {
    return String(s ?? '')
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '')
        .trim()
        .replace(/\s+/g, '_');
}

// DD.MM.YYYY
function formatDeDate(val) {
    const s = String(val ?? '').trim();
    if (!s) return '—';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    try {
        return new Intl.DateTimeFormat('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(d);
    } catch {
        return '—';
    }
}

function parseTemplateJson(input) {
    if (!input) return {grouped: {groups: []}, behavior: {items: []}};
    let parsed = input;

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            parsed = null;
        }
    }

    const tpl = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    const sections = Array.isArray(tpl.sections) ? tpl.sections : [];

    const grouped =
        sections.find(s => s?.type === 'grouped_competencies' || s?.key === 'fachkompetenz') || {groups: []};
    const behavior =
        sections.find(s => s?.type === 'behavior_items' || s?.key === 'verhalten') || {items: []};

    return {
        title: safeStr(tpl.title, 'Rückmeldungsbogen'),
        grouped: {
            key: grouped.key ?? 'fachkompetenz',
            label: grouped.label ?? 'Kompetenzen',
            groups: (Array.isArray(grouped.groups) ? grouped.groups : []).map(g => ({
                id: g?.id ?? '',
                title: safeStr(g?.title, ''),
                items: (Array.isArray(g?.items) ? g.items : []).map(it => ({
                    id: it?.id ?? '',
                    label: safeStr(it?.label, ''),
                })),
            })),
        },
        behavior: {
            key: behavior.key ?? 'verhalten',
            label: behavior.label ?? 'Arbeits- und Sozialverhalten',
            items: (Array.isArray(behavior.items) ? behavior.items : []).map(it => ({
                id: it?.id ?? '',
                label: safeStr(it?.label, ''),
            })),
        },
    };
}

function drawHeader(doc, data, pageW) {
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Rückmeldungsbogen', pageW / 2, 48, {align: 'center'});

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Freie Naturschule Barnim', pageW / 2, 68, {align: 'center'});

    const marginX = 56.7; // 2cm in pt
    const rightX = pageW - marginX;

    doc.setFontSize(10);

    doc.setFont('helvetica', 'bold');
    doc.text('Datum:', rightX - 90, 44);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDeDate(data.date), rightX, 44, {align: 'right'});

    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(data.student, '—'), rightX, 60, {align: 'right'});

    doc.setFontSize(11);

    doc.setFont('helvetica', 'bold');
    doc.text('Fach:', marginX, 94);
    doc.setFont('helvetica', 'normal');
    doc.text(safeStr(data.subject, ''), marginX + 60, 94);

    doc.setFont('helvetica', 'bold');
    doc.text('Thema:', marginX, 114);
    doc.setFont('helvetica', 'normal');

    const topicX = marginX + 60;
    const topicY = 114;
    const topicMaxW = Math.max(0, rightX - topicX);
    const topicLineH = 14.4; // 12pt * 1.2

    const topic = safeStr(data.topic, '');
    const topicLinesRaw = doc.splitTextToSize(topic, topicMaxW);
    const topicLines = Array.isArray(topicLinesRaw) ? topicLinesRaw : [String(topic)];

    const topicMaxLines = 5;
    const outLines = topicLines.slice(0, topicMaxLines);
    if (topicLines.length > topicMaxLines && outLines.length > 0) {
        const lastIdx = outLines.length - 1;
        outLines[lastIdx] = String(outLines[lastIdx]).replace(/\s+$/, '').replace(/\.{3}$/, '') + '…';
    }

    outLines.forEach((line, i) => {
        doc.text(String(line), topicX, topicY + (i * topicLineH));
    });

    const baseTopY = 132;
    const extraTop = Math.max(0, outLines.length - 1) * topicLineH;
    return baseTopY + extraTop;
}

/**
 * Pfeil wie im Bild:
 * - dünne Linie mit Pfeilspitze rechts
 * - optionaler Marker (kleines Dreieck) bei Prozentwert
 */
function drawArrowScale(doc, x, y, w, h, percent = null, {showMarker = false} = {}) {
    const midY = y + (h / 2);
    const headL = Math.min(12, Math.max(8, w * 0.06));
    const lineEndX = x + w - headL;

    doc.setDrawColor(0);
    doc.setLineWidth(1);

    // Hauptlinie
    doc.line(x, midY, lineEndX, midY);

    // Pfeilspitze (gefülltes Dreieck)
    doc.setFillColor(0);
    doc.triangle(
        lineEndX, midY - 5,
        lineEndX, midY + 5,
        x + w, midY,
        'F'
    );

    // Marker (kleines Dreieck nach unten)
    if (showMarker && percent !== null && percent !== undefined) {
        const p = clampPct(percent);
        const mx = x + ((lineEndX - x) * p) / 100;
        const my = midY;

        doc.triangle(
            mx - 4, my - 8,
            mx + 4, my - 8,
            mx, my - 1,
            'F'
        );
    }
}

function drawSectionTitleBox(doc, x, y, w, h, title, rightHint) {
    doc.setLineWidth(1);
    doc.rect(x, y, w, h);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(title, x + 10, y + 22);

    if (rightHint) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(rightHint, x + w - 10, y + 20, {align: 'right'});
    }
}

function drawCompetenciesHeaderRow(doc, x, y, w, colLabelW, colAbsentW, title, rightHint) {
    const h = 34;

    // NEU: Header-Hintergrund wie im Raster-PDF (Grau 242) füllen
    doc.setFillColor(242);
    doc.rect(x, y, w, h, 'F');

    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.rect(x, y, w, h);

    // vertikale Trenner
    doc.line(x + colLabelW, y, x + colLabelW, y + h);
    doc.line(x + colLabelW + colAbsentW, y, x + colLabelW + colAbsentW, y + h);

    // Links: Titel
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(title, x + 10, y + 22);

    // Mitte: ab-wesend (zweizeilig)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const midX = x + colLabelW + (colAbsentW / 2);
    doc.text('ab-', midX, y + 14, {align: 'center'});
    doc.text('wesend', midX, y + 26, {align: 'center'});

    // Rechts: Hinweistext + Pfeil (wie im Bild)
    if (rightHint) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(rightHint, x + colLabelW + colAbsentW + 22, y + 20, {align: 'left'});
    }
}

function wrapTextLines(doc, text, maxWidth) {
    const t = safeStr(text, '');
    if (!t) return [''];
    const lines = doc.splitTextToSize(t, maxWidth);
    return (Array.isArray(lines) && lines.length) ? lines : [''];
}

function computeRowHeight(linesCount, {lineHeight, paddingTop, paddingBottom, minHeight}) {
    const hh = paddingTop + (linesCount * lineHeight) + paddingBottom;
    return Math.max(minHeight, hh);
}

async function drawCompetenciesTable(doc, x, y, w, data, onNeedNewPage) {
    const colAbsentW = 44;
    const colArrowW = 220;
    const colLabelW = w - colAbsentW - colArrowW;

    const labelFontSize = 11;
    const labelLineHeight = 14.2; // 12pt * 1.2
    const labelPaddingTop = 6;
    const labelPaddingBottom = 6;

    const minItemRowH = 26;
    const groupRowH = 26;

    const pageBottomY = 780;

    drawCompetenciesHeaderRow(
        doc,
        x,
        y,
        w,
        colLabelW,
        colAbsentW,
        'KOMPETENZEN',
        'für die Einschätzung den Pfeilschaft nutzen'
    );
    let cy = y + 34;

    const groups = Array.isArray(data.templateSnapshot?.grouped?.groups) ? data.templateSnapshot.grouped.groups : [];

    for (const g of groups) {
        const groupItems = Array.isArray(g.items) ? g.items : [];

        // Group-Header nicht verwaisen lassen -> muss mit mind. 1 Item (falls vorhanden) auf die Seite passen
        let firstItemRowH = 0;
        if (groupItems.length > 0) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(labelFontSize);

            const labelMaxWidth = colLabelW - 20;
            const firstLines = wrapTextLines(doc, groupItems[0].label, labelMaxWidth);
            firstItemRowH = computeRowHeight(firstLines.length, {
                lineHeight: labelLineHeight,
                paddingTop: labelPaddingTop,
                paddingBottom: labelPaddingBottom,
                minHeight: minItemRowH,
            });
        }

        const requiredBeforeFirstItem = groupRowH + firstItemRowH;
        if (cy + requiredBeforeFirstItem > pageBottomY) {
            cy = await onNeedNewPage();
            drawCompetenciesHeaderRow(
                doc,
                x,
                cy,
                w,
                colLabelW,
                colAbsentW,
                'KOMPETENZEN',
                ''
            );
            cy += 34;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(labelFontSize);
        doc.rect(x, cy, w, groupRowH);
        doc.text(safeStr(g.title, ''), x + 10, cy + 17);
        cy += groupRowH;

        for (const it of groupItems) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(labelFontSize);

            const labelMaxWidth = colLabelW - 20;
            const lines = wrapTextLines(doc, it.label, labelMaxWidth);

            const rowH = computeRowHeight(lines.length, {
                lineHeight: labelLineHeight,
                paddingTop: labelPaddingTop,
                paddingBottom: labelPaddingBottom,
                minHeight: minItemRowH,
            });

            if (cy + rowH > pageBottomY) {
                cy = await onNeedNewPage();
                drawCompetenciesHeaderRow(
                    doc,
                    x,
                    cy,
                    w,
                    colLabelW,
                    colAbsentW,
                    'KOMPETENZEN',
                    ''
                );
                cy += 34;
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(labelFontSize);
            }

            // Row borders
            doc.rect(x, cy, w, rowH);
            doc.line(x + colLabelW, cy, x + colLabelW, cy + rowH);
            doc.line(x + colLabelW + colAbsentW, cy, x + colLabelW + colAbsentW, cy + rowH);

            // Text mehrzeilig
            const textX = x + 10;
            let textY = cy + labelPaddingTop + labelFontSize;
            for (const line of lines) {
                doc.text(String(line), textX, textY, {maxWidth: labelMaxWidth});
                textY += labelLineHeight;
            }

            // Checkbox (leer, Formular)
            const cbSize = 10;
            const cbX = x + colLabelW + 17;
            const cbY = cy + (rowH - cbSize) / 2;
            doc.rect(cbX, cbY, cbSize, cbSize);

            // Pfeil in der Pfeil-Spalte (ohne Marker, Formular)
            const arrowH = 14;
            const barPadL = 12;
            const barPadR = 12;

            const cellLeft = x + colLabelW + colAbsentW;
            const arrowX = cellLeft + barPadL;
            const arrowY = cy + (rowH - arrowH) / 2;
            const arrowW = Math.max(0, colArrowW - barPadL - barPadR);

            drawArrowScale(doc, arrowX, arrowY, arrowW, arrowH, null, {showMarker: false});

            cy += rowH;
        }
    }

    return cy;
}

async function drawBehaviorTable(doc, x, y, w, data, onNeedNewPage) {
    const colArrowW = 220;
    const colLabelW = w - colArrowW;

    const labelFontSize = 11;
    const labelLineHeight = 14.2; // 12pt * 1.2
    const labelPaddingTop = 6;
    const labelPaddingBottom = 6;

    const minItemRowH = 28;
    const headerH = 34;
    const pageBottomY = 780;

    const items = Array.isArray(data.templateSnapshot?.behavior?.items) ? data.templateSnapshot.behavior.items : [];

    // NEU: Wenn keine Items vorhanden sind, nichts drucken (auch keinen Spaltenkopf)
    if (items.length === 0) {
        return y;
    }

    // Vorab prüfen: passt Header + mind. 1 Item noch auf die Seite?
    let firstRowH = minItemRowH;
    if (items.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(labelFontSize);

        const labelMaxWidth = colLabelW - 20;
        const firstLines = wrapTextLines(doc, items[0].label, labelMaxWidth);
        firstRowH = computeRowHeight(firstLines.length, {
            lineHeight: labelLineHeight,
            paddingTop: labelPaddingTop,
            paddingBottom: labelPaddingBottom,
            minHeight: minItemRowH,
        });
    }

    if (y + headerH + (items.length > 0 ? firstRowH : 0) > pageBottomY) {
        y = await onNeedNewPage();
    }

    drawSectionTitleBox(
        doc,
        x,
        y,
        w,
        headerH,
        'ARBEITS- UND SOZIALVERHALTEN',
        ''
    );
    let cy = y + headerH;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(labelFontSize);

    for (const it of items) {
        const labelMaxWidth = colLabelW - 20;
        const lines = wrapTextLines(doc, it.label, labelMaxWidth);
        const rowH = computeRowHeight(lines.length, {
            lineHeight: labelLineHeight,
            paddingTop: labelPaddingTop,
            paddingBottom: labelPaddingBottom,
            minHeight: minItemRowH,
        });

        if (cy + rowH > pageBottomY) {
            cy = await onNeedNewPage();
            drawSectionTitleBox(
                doc,
                x,
                cy,
                w,
                headerH,
                'ARBEITS- UND SOZIALVERHALTEN',
                ''
            );

            cy += headerH;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(labelFontSize);
        }

        doc.rect(x, cy, w, rowH);
        doc.line(x + colLabelW, cy, x + colLabelW, cy + rowH);

        const textX = x + 10;
        let textY = cy + labelPaddingTop + labelFontSize;

        for (const line of lines) {
            doc.text(String(line), textX, textY, {maxWidth: labelMaxWidth});
            textY += labelLineHeight;
        }

        const arrowH = 14;
        const padL = 12;
        const padR = 12;

        const cellLeft = x + colLabelW;
        const arrowX = cellLeft + padL;
        const arrowY = cy + (rowH - arrowH) / 2;
        const arrowW = Math.max(0, colArrowW - padL - padR);

        drawArrowScale(doc, arrowX, arrowY, arrowW, arrowH, null, {showMarker: false});

        cy += rowH;
    }

    return cy;
}

function drawAnlagePage(doc, {startY = 50, compact = false} = {}) {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const marginX = 60;

    doc.setTextColor(0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    // Block 1: Frage + Linien
    const title1 = 'Was nehme ich aus diesem Thema mit (Inspiration, Erkenntnisse, Wissen, Erfahrungen)?';
    doc.text(title1, marginX, startY);

    let y = startY + 26;

    const block1Lines = compact ? 3 : 5;
    const block1Gap = compact ? 22 : 24;

    for (let i = 0; i < block1Lines; i += 1) {
        doc.setLineWidth(0.8);
        doc.line(marginX, y, pageW - marginX, y);
        y += block1Gap;
    }

    // Block 2: Anmerkungen + Linien (automatisch weniger Linien, wenn weniger Platz)
    y += compact ? 24 : 34;
    doc.text('Anmerkungen:', marginX, y);
    y += 24;

    const linesBottomLimit = pageH - (compact ? 140 : 170); // Platz für Signaturen lassen
    const lineStep = compact ? 24 : 22;

    // Anzahl Zeilen begrenzen (reduziert sich automatisch, wenn startY weiter unten liegt)
    const available = Math.max(0, linesBottomLimit - y);
    const maxLines = Math.floor(available / lineStep) + (compact ? 1 : 0);

    for (let i = 0; i < maxLines; i += 1) {
        doc.setLineWidth(0.8);
        doc.line(marginX, y, pageW - marginX, y);
        y += lineStep;
    }

    // Signaturen (2 Spalten)
    const sigY = pageH - 80;
    const colGap = 80;
    const colW = (pageW - marginX * 2 - colGap) / 2;

    // Links
    doc.setLineWidth(0.8);
    doc.line(marginX, sigY, marginX + colW, sigY);
    doc.setFontSize(10);
    doc.text('Unterschrift Sekie', marginX, sigY + 16);

    // Rechts
    const rightX = marginX + colW + colGap;
    doc.line(rightX, sigY, rightX + colW, sigY);
    doc.text('Unterschrift Lernbegleitung', rightX, sigY + 16);
}

function drawFooterPageNumber(doc, pageNumber, totalPages) {
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const label = `Seite ${pageNumber} / ${totalPages}`;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(label, pageW / 2, pageH - 24, {align: 'center'});
}

/**
 * payload:
 *  - template: { id, subject?, topic?, title?, template_json? }
 *  - students: Array<{id, first_name, last_name, student_number?}>
 *  - date: 'YYYY-MM-DD'
 */
export async function exportFeedbackFormsPdf({template, students, date}) {
    const tpl = template || null;
    const list = Array.isArray(students) ? students : [];
    if (!tpl || list.length === 0) return;

    const fullTemplate = tpl?.template_json ? tpl : await getTemplate(tpl.id);
    const templateSnapshot = parseTemplateJson(fullTemplate?.template_json);

    const doc = new jsPDF({unit: 'pt', format: 'a4', orientation: 'portrait'});
    const ranges = [];

    for (let i = 0; i < list.length; i += 1) {
        const s = list[i];
        const studentName = `${safeStr(s.first_name, '')} ${safeStr(s.last_name, '')}`.replace(/^,\s*/, '').trim();

        if (i > 0) doc.addPage('a4', 'portrait');
        const startPage = doc.internal.getNumberOfPages();

        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const marginX = 56.7; // 2cm in pt
        const leftX = marginX;
        const leftW = pageW - (2 * marginX);

        const data = {
            date: date || '',
            student: studentName || '—',
            subject: safeStr(fullTemplate?.subject || '', ''),
            topic: safeStr(fullTemplate?.topic || fullTemplate?.title || '', ''),
            templateSnapshot,
        };

        let topY = drawHeader(doc, data, pageW);

        const onNeedNewPage = async () => {
            doc.addPage('a4', 'portrait');
            topY = drawHeader(doc, data, pageW);
            return topY;
        };

        let y = topY;
        y = await drawCompetenciesTable(doc, leftX, y, leftW, data, onNeedNewPage);

        y += 16;
        y = await drawBehaviorTable(doc, leftX, y, leftW, data, onNeedNewPage);

        // Wenn auf der aktuellen (2.) Seite noch ca. 50% frei sind, Anlage dort "reinziehen" (kompakt),
        // sonst eigene Seite.
        const remaining = pageH - y;
        const hasHalfPageFree = remaining >= (pageH * 0.45);

        if (hasHalfPageFree) {
            const anlageStartY = y + 42;
            drawAnlagePage(doc, {startY: anlageStartY, compact: true});
        } else {
            doc.addPage('a4', 'portrait');
            drawAnlagePage(doc, {startY: 50, compact: false});
        }

        const endPage = doc.internal.getNumberOfPages();
        ranges.push({startPage, endPage});
    }

    for (const r of ranges) {
        const total = r.endPage - r.startPage + 1;
        for (let p = r.startPage; p <= r.endPage; p += 1) {
            doc.setPage(p);
            drawFooterPageNumber(doc, p - r.startPage + 1, total);
        }
    }

    const fname = `Rueckmeldungsboegen_${fileSafe(`${fullTemplate?.subject}_${fullTemplate?.id}`)}.pdf`;
    doc.save(fname);
}